import { Controller } from "@hotwired/stimulus"
import { Chart, registerables } from "chart.js"

Chart.register(...registerables)

function dig(obj, path) {
  return path.split(".").reduce((acc, key) => (acc == null ? undefined : acc[key]), obj)
}

export default class extends Controller {
  static targets = ["sessionsChart", "durationChart", "costChart", "tokensChart"]
  static values = {
    chartsDataUrl: String,
    labelSessions: String,
    labelAvgDuration: String,
    labelCost: String,
    labelGrokTokens: String,
    labelHostagentTokens: String,
    locale: { type: String, default: "hu" },
  }

  connect() {
    this.currentPeriod = "weekly"
    this.instances = {}
    this.loadChartData()
  }

  switchPeriod(event) {
    const period = event.currentTarget.dataset.period
    if (period === this.currentPeriod) return

    this.currentPeriod = period
    event.currentTarget.parentElement.querySelectorAll("button").forEach((btn) => {
      btn.classList.remove("active")
    })
    event.currentTarget.classList.add("active")
    this.loadChartData()
  }

  async loadChartData() {
    try {
      const url = new URL(this.chartsDataUrlValue, window.location.origin)
      // Admin weekly/monthly toggle; tenant report keeps period from the URL/filter.
      if (this.element.querySelector("[data-period]")) {
        url.searchParams.set("period", this.currentPeriod)
      }
      const response = await fetch(url.toString(), { credentials: "same-origin" })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const data = await response.json()
      this.renderCharts(data)
    } catch (error) {
      console.error("[CallCenterCharts] Error loading chart data:", error)
      this.renderCharts({
        labels: [],
        session_counts: [],
        avg_duration_seconds: [],
        costs: [],
        realtime_tokens: [],
        hostagent_tokens: [],
      })
    }
  }

  renderCharts(data) {
    const labels = this._formatLabels(data.labels || [])
    this._renderBar(
      "sessionsChart",
      labels,
      data.session_counts || [],
      this._label("labelSessions", "activity.chart_labels.sessions", "Sessions"),
      "rgba(54, 162, 235, 0.7)"
    )
    this._renderBar(
      "durationChart",
      labels,
      data.avg_duration_seconds || [],
      this._label("labelAvgDuration", "activity.chart_labels.avg_duration", "Avg duration (s)"),
      "rgba(75, 192, 192, 0.7)"
    )
    this._renderBar(
      "costChart",
      labels,
      data.costs || [],
      this._label("labelCost", "activity.chart_labels.cost", "Cost ($)"),
      "rgba(255, 159, 64, 0.7)"
    )
    this._renderStackedTokens(labels, data.realtime_tokens || [], data.hostagent_tokens || [])
  }

  _label(valueName, i18nPath, fallback) {
    const fromValue = this[`${valueName}Value`]
    if (fromValue && String(fromValue).trim() !== "") return fromValue

    const fromI18n = dig(window.I18n?.call_center, i18nPath)
    if (fromI18n && String(fromI18n).trim() !== "") return fromI18n

    return fallback
  }

  _renderBar(targetName, labels, values, label, color) {
    const hasTarget = this[`has${targetName.charAt(0).toUpperCase()}${targetName.slice(1)}Target`]
    if (!hasTarget) return
    const canvas = this[`${targetName}Target`]

    if (this.instances[targetName]) {
      this.instances[targetName].destroy()
    }

    this.instances[targetName] = new Chart(canvas.getContext("2d"), {
      type: "bar",
      data: {
        labels,
        datasets: [{ label, data: values, backgroundColor: color, borderWidth: 1 }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        locale: this._chartLocale(),
        plugins: { legend: { position: "top" } },
        scales: { y: { beginAtZero: true } },
      },
    })
  }

  _renderStackedTokens(labels, grokTokens, hostagent) {
    if (!this.hasTokensChartTarget) return
    if (this.instances.tokensChart) this.instances.tokensChart.destroy()

    this.instances.tokensChart = new Chart(this.tokensChartTarget.getContext("2d"), {
      type: "bar",
      data: {
        labels,
        datasets: [
          {
            label: this._label("labelGrokTokens", "activity.chart_labels.grok_tokens", "Grok tokens"),
            data: grokTokens,
            backgroundColor: "rgba(153, 102, 255, 0.7)",
            stack: "tokens",
          },
          {
            label: this._label(
              "labelHostagentTokens",
              "activity.chart_labels.hostagent_tokens",
              "Hostagent tokens"
            ),
            data: hostagent,
            backgroundColor: "rgba(255, 99, 132, 0.7)",
            stack: "tokens",
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        locale: this._chartLocale(),
        plugins: { legend: { position: "top" } },
        scales: {
          x: { stacked: true },
          y: { stacked: true, beginAtZero: true },
        },
      },
    })
  }

  _chartLocale() {
    return (
      this.localeValue ||
      document.documentElement.lang ||
      window.I18n?.locale ||
      "hu"
    )
  }

  _formatLabels(labels) {
    const locale = this._chartLocale()
    return labels.map((label) => {
      if (/^\d{4}-\d{2}$/.test(label)) return label
      try {
        const d = new Date(label)
        if (Number.isNaN(d.getTime())) return label
        return d.toLocaleDateString(locale)
      } catch (_) {
        return label
      }
    })
  }
}
